import { useEffect, useState } from "react";
import {
  Text,
  Paper,
  Box,
  Group,
  ThemeIcon,
  Flex,
  NumberInput,
  Button,
  Loader,
  Badge,
} from "@mantine/core";
import {
  IconSettings,
  IconClock,
  IconBell,
  IconDeviceFloppy,
} from "@tabler/icons";
import { useApiUrl, useCustom, useCustomMutation } from "@refinedev/core";
import { showNotification } from "@mantine/notifications";

/* ═══════════════════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════════════════ */

type ReviewSetting = {
  id: number;
  name: string;
  value: { value: string };
};

/* ═══════════════════════════════════════════════════════════════════════════
   Setting labels & descriptions
   ═══════════════════════════════════════════════════════════════════════════ */

const SETTING_META: Record<
  string,
  { label: string; description: string; icon: React.ReactNode; color: string }
> = {
  review_first_reminder_delay_days: {
    label: "First Reminder Delay",
    description:
      "Number of days after the initial review request is sent before the first reminder is eligible to be sent.",
    icon: <IconClock size={16} />,
    color: "grape",
  },
  review_second_reminder_delay_days: {
    label: "Second Reminder Delay",
    description:
      "Number of days after the first reminder is sent before the second reminder is eligible to be sent.",
    icon: <IconBell size={16} />,
    color: "orange",
  },
};

/* ═══════════════════════════════════════════════════════════════════════════
   Setting row component
   ═══════════════════════════════════════════════════════════════════════════ */

function SettingRow({
  setting,
  editValue,
  onEditChange,
  onSave,
  isSaving,
}: {
  setting: ReviewSetting;
  editValue: number;
  onEditChange: (val: number) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const meta = SETTING_META[setting.name];
  const currentValue = parseInt(setting.value.value, 10);
  const hasChanged = editValue !== currentValue;

  return (
    <Paper
      p="lg"
      radius="md"
      sx={(theme) => ({
        border: `1px solid ${theme.colorScheme === "dark"
          ? theme.colors.dark[4]
          : theme.colors.gray[2]
          }`,
      })}
    >
      <Flex
        align="flex-start"
        justify="space-between"
        gap="lg"
        wrap="nowrap"
      >
        {/* Left side — info */}
        <Group spacing="sm" noWrap sx={{ flex: 1 }}>
          <ThemeIcon
            size={36}
            radius="md"
            variant="light"
            color={meta?.color ?? "gray"}
          >
            {meta?.icon ?? <IconSettings size={16} />}
          </ThemeIcon>
          <Box>
            <Group spacing="xs" noWrap>
              <Text size="sm" weight={700}>
                {meta?.label ?? setting.name}
              </Text>
              <Badge
                variant="outline"
                color="gray"
                size="xs"
                radius="xl"
                sx={{ fontFamily: "monospace" }}
              >
                {setting.name}
              </Badge>
            </Group>
            <Text size="xs" color="dimmed" mt={4}>
              {meta?.description ?? ""}
            </Text>
          </Box>
        </Group>

        {/* Right side — input + save */}
        <Group spacing="sm" noWrap>
          <NumberInput
            value={editValue}
            onChange={(val) => onEditChange(typeof val === "number" ? val : 0)}
            min={1}
            max={365}
            size="sm"
            sx={{ width: 100 }}
            styles={(theme) => ({
              input: {
                fontFamily: "monospace",
                fontWeight: 700,
                textAlign: "center",
              },
            })}
            rightSection={
              <Text size={10} color="dimmed" mr={4}>
                days
              </Text>
            }
            rightSectionWidth={36}
          />
          <Button
            size="sm"
            variant={hasChanged ? "filled" : "light"}
            color={hasChanged ? "blue" : "gray"}
            leftIcon={
              isSaving ? (
                <Loader size={14} color="white" />
              ) : (
                <IconDeviceFloppy size={14} />
              )
            }
            disabled={!hasChanged || isSaving}
            onClick={onSave}
          >
            Save
          </Button>
        </Group>
      </Flex>
    </Paper>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main settings component
   ═══════════════════════════════════════════════════════════════════════════ */

export const ReviewRequestSettings: React.FC = () => {
  const apiUrl = useApiUrl();
  const [editValues, setEditValues] = useState<Record<number, number>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const {
    data: settingsData,
    isFetching,
    refetch,
  } = useCustom<ReviewSetting[]>({
    url: `${apiUrl}/review_settings`,
    method: "get",
    queryOptions: { enabled: true },
  });

  const { mutate } = useCustomMutation();

  const settings = settingsData?.data ?? [];

  // Sync edit values from server data when it loads
  useEffect(() => {
    if (Array.isArray(settings) && settings.length > 0) {
      setEditValues((prev) => {
        const next = { ...prev };
        settings.forEach((s) => {
          if (next[s.id] === undefined) {
            const parsed = parseInt(s.value.value, 10);
            next[s.id] = Number.isNaN(parsed) ? 7 : parsed;
          }
        });
        return next;
      });
    }
  }, [settings]);

  const getEditValue = (setting: ReviewSetting): number => {
    return editValues[setting.id] ?? 7;
  };

  const handleSave = (setting: ReviewSetting) => {
    const newValue = getEditValue(setting);
    setSavingId(setting.id);

    mutate(
      {
        method: "put",
        url: `${apiUrl}/review_settings/${setting.id}`,
        values: { value: { value: String(newValue) } },
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Setting Updated",
            message: `${SETTING_META[setting.name]?.label ?? setting.name} set to ${newValue} days`,
            color: "green",
          });
          // Clear local edit so it picks up the fresh server value
          setEditValues((prev) => {
            const next = { ...prev };
            delete next[setting.id];
            return next;
          });
          refetch();
        },
        onError: () => {
          showNotification({
            title: "Error",
            message: "Failed to update setting. Please try again.",
            color: "red",
          });
        },
        onSettled: () => {
          setSavingId(null);
        },
      }
    );
  };

  return (
    <Box>
      {/* ── Header ────────────────────────────────────────────────── */}
      <Paper
        p="lg"
        radius="md"
        mb="lg"
        sx={(theme) => ({
          background:
            theme.colorScheme === "dark"
              ? `linear-gradient(135deg, ${theme.colors.dark[7]} 0%, ${theme.fn.rgba(theme.colors.grape[9], 0.12)} 100%)`
              : `linear-gradient(135deg, ${theme.white} 0%, ${theme.colors.grape[0]} 100%)`,
          border: `1px solid ${theme.colorScheme === "dark"
            ? theme.colors.dark[4]
            : theme.colors.grape[1]
            }`,
        })}
      >
        <Group position="apart" noWrap>
          <Group spacing="md" noWrap>
            <ThemeIcon size={48} radius="xl" variant="light" color="grape">
              <IconSettings size={24} />
            </ThemeIcon>
            <Box>
              <Text size="lg" weight={700}>
                Review Request Settings
              </Text>
              <Text size="sm" color="dimmed">
                Configure review reminder timing and intervals
              </Text>
            </Box>
          </Group>
          {isFetching && <Loader size="sm" color="grape" />}
        </Group>
      </Paper>

      {/* ── Loading state ─────────────────────────────────────────── */}
      {isFetching && settings.length === 0 && (
        <Flex justify="center" py={80}>
          <Loader size="lg" color="grape" />
        </Flex>
      )}

      {/* ── Settings list ─────────────────────────────────────────── */}
      {settings.length > 0 && (
        <Flex direction="column" gap="md">
          {settings.map((setting) => (
            <SettingRow
              key={setting.id}
              setting={setting}
              editValue={getEditValue(setting)}
              onEditChange={(val) =>
                setEditValues((prev) => ({ ...prev, [setting.id]: val }))
              }
              onSave={() => handleSave(setting)}
              isSaving={savingId === setting.id}
            />
          ))}
        </Flex>
      )}

      {/* ── Empty state ───────────────────────────────────────────── */}
      {!isFetching && settings.length === 0 && (
        <Paper
          p="xl"
          radius="md"
          sx={(theme) => ({
            border: `1px solid ${theme.colorScheme === "dark"
              ? theme.colors.dark[4]
              : theme.colors.gray[2]
              }`,
            textAlign: "center",
          })}
        >
          <Text size="sm" color="dimmed">
            No review settings found. Settings will appear here once configured
            in the database.
          </Text>
        </Paper>
      )}
    </Box>
  );
};
